import { yupResolver } from "@hookform/resolvers/yup";
import {
  Button,
  Grid,
  IconButton,
  TextField,
  Typography,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import HighlightOffIcon from "@material-ui/icons/HighlightOff";
import { postInformations } from "adapters";
import { DialogWrapper } from "components/shared/dialog";
import { ModuleSettings } from "components/shared/module-settings";
import { PermissionsControl } from "components/shared/permission-control";
import { SectionInfoTooltip } from "components/shared/section-info-tooltip";
import { SECTION_TOOLTIPS } from "components/url-desinger/section-tooltips";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import {
  GetUrlBuilderData,
  InfoLinks,
  selectBuilderPermissions,
  SetUrlBuilderData,
} from "store/url-builder";
import { findPermissionByModule } from "utils/permissions";
import * as yup from "yup";

interface InformationFormData {
  location: string | null;
  calendar: string | null;
}

const schemaInformation = yup.object().shape({
  location: yup.string().nullable().label("Location"),
  calendar: yup.string().nullable().label("Calendar"),
});

export interface EditInformationDialogProps {
  isOpen: boolean;
  onClose(): void;
  builderType: string;
}

export const EditInformationDialog = ({
  isOpen,
  onClose,
  builderType,
}: EditInformationDialogProps) => {
  const dispatch = useDispatch();
  const builderData = useSelector(GetUrlBuilderData);
  const builderPermissions = useSelector(selectBuilderPermissions);
  const [loading, setLoading] = useState(false);
  const [links, setLinks] = useState<InfoLinks[]>(builderData.info.links);
  const { cardId }: { cardId: string } = useParams();

  const hasPopulatedValue = (value?: string | null) =>
    typeof value === "string" && value.trim().length > 0;

  const hasExistingInformation =
    hasPopulatedValue(builderData.info?.location) ||
    hasPopulatedValue(builderData.info?.calendar) ||
    (builderData.info?.links ?? []).some(
      ({ title, url }) => hasPopulatedValue(title) || hasPopulatedValue(url)
    );

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schemaInformation),
    defaultValues: builderData.info,
    mode: "onBlur",
  });

  const handleLinkChange = (link: InfoLinks, index: number) => {
    const updatedLinks = [...links];
    updatedLinks[index] = link;
    setLinks(updatedLinks);
  };

  const addLink = () => setLinks([...links, { title: "", url: "" }]);
  const removeLink = (idx: number) =>
    setLinks(links.filter((link, index) => idx !== index));

  const submit = (formData: InformationFormData) => {
    setLoading(true);
    postInformations(cardId, builderType, { ...formData, links })
      .then(({ data }) => {
        if (!data.success) return;
        dispatch(SetUrlBuilderData(data.data));
        onClose();
      })
      .finally(() => setLoading(false));
  };

  return (
    <DialogWrapper
      open={isOpen}
      onClose={(event, reason) => {
        if (reason && reason === "backdropClick") return false;
        else onClose();
      }}
      onSubmit={handleSubmit(submit)}
      loading={loading}
      title={hasExistingInformation ? "Add/Edit Information" : "Add Information"}
    >
      <Grid item xs={12} className="mb-3">
        <Controller
          control={control as any}
          name="location"
          render={({ field: { onChange, value } }: any) => (
            <>
              <Typography variant="subtitle2">
                Location <SectionInfoTooltip content={SECTION_TOOLTIPS.information_address} marginLeft={"0px"} />
                <ModuleSettings
                  permissions={findPermissionByModule(5, builderPermissions)}
                  builderType={builderData.urlDesignType}
                />
              </Typography>
              <PermissionsControl
                permissions={findPermissionByModule(5, builderPermissions)}
                builderType={builderType}
              >
                <TextField
                  onChange={onChange}
                  value={value}
                  error={!!errors.location}
                  helperText={errors.location?.message}
                  fullWidth
                  disabled={loading}
                />
              </PermissionsControl>
            </>
          )}
        />
      </Grid>
      <Grid item xs={12} className="mb-3">
        <Controller
          control={control as any}
          name="calendar"
          render={({ field: { onChange, value } }: any) => (
            <>
              <Typography variant="subtitle2">
                Calendar <SectionInfoTooltip content={SECTION_TOOLTIPS.information_calendar} marginLeft={"0px"} />
                <ModuleSettings
                  permissions={findPermissionByModule(6, builderPermissions)}
                  builderType={builderData.urlDesignType}
                />
              </Typography>
              <PermissionsControl
                permissions={findPermissionByModule(6, builderPermissions)}
                builderType={builderType}
              >
                <TextField
                  onChange={onChange}
                  value={value}
                  error={!!errors.calendar}
                  helperText={errors.calendar?.message}
                  fullWidth
                  disabled={loading}
                />
              </PermissionsControl>
            </>
          )}
        />
      </Grid>
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom>
          Links{" "} <SectionInfoTooltip content={SECTION_TOOLTIPS.information_links} marginLeft={"0px"} />
          <ModuleSettings
            permissions={findPermissionByModule(8, builderPermissions)}
            builderType={builderData.urlDesignType}
          />
        </Typography>
      </Grid>
      <PermissionsControl
        permissions={findPermissionByModule(8, builderPermissions)}
        builderType={builderType}
      >
        <Grid container item xs={12} spacing={2} className="mb-3">
          {links.map((link, idx) => (
            <>
              <Grid item>
                <Typography variant="subtitle2">Title</Typography>
                <TextField
                  onChange={(e) =>
                    handleLinkChange({ ...link, title: e.target.value }, idx)
                  }
                  value={link.title}
                  fullWidth
                  disabled={loading}
                />
              </Grid>
              <Grid item>
                <Typography variant="subtitle2">URL</Typography>
                <TextField
                  onChange={(e) =>
                    handleLinkChange({ ...link, url: e.target.value }, idx)
                  }
                  value={link.url}
                  fullWidth
                  disabled={loading}
                />
              </Grid>
              <Grid item container alignItems="flex-end" xs={1}>
                <IconButton onClick={() => removeLink(idx)} className="mb-1">
                  <HighlightOffIcon color="error" />
                </IconButton>
              </Grid>
            </>
          ))}
        </Grid>
        <Grid item container xs={12} justifyContent="center">
          <Button onClick={addLink} style={{ textTransform: "none" }}>
            <AddIcon /> Add New Link
          </Button>
        </Grid>
      </PermissionsControl>
    </DialogWrapper>
  );
};
